import React, { useState, useEffect } from 'react';
import { Grid, Typography, TextField, Button } from '@mui/material';
import { useGetSettingsQuery, useUpdateSettingsMutation } from 'state/api';
import { useSnack, SaveSnackbar } from './settingsHelper';

// Genuine store-level application settings (identity + contact details).
// The API stores store identity under `businessDetails`; user edits are
// persisted through the settings API under `custom.appSettings`, which then
// takes precedence over the seeded businessDetails on subsequent loads.
const formatAddress = (addr) => {
  if (!addr) return '';
  if (typeof addr === 'string') return addr;
  return ['line1', 'line2', 'city', 'state', 'country', 'pincode']
    .map((k) => addr[k])
    .filter(Boolean)
    .join(', ');
};

const AppSettings = () => {
  const { data } = useGetSettingsQuery();
  const settings = data?.data || {};
  const [updateSettings, { isLoading }] = useUpdateSettingsMutation();
  const { snack, showSuccess, showError, closeSnack } = useSnack();

  const [storeName, setStoreName] = useState('');
  const [supportEmail, setSupportEmail] = useState('');
  const [supportPhone, setSupportPhone] = useState('');
  const [storeAddress, setStoreAddress] = useState('');

  useEffect(() => {
    const bd = settings.businessDetails || {};
    const s = settings.custom?.appSettings || {};
    // Prefer a value the user has explicitly saved, otherwise fall back to the
    // store identity the API already returns under businessDetails.
    setStoreName(s.storeName ?? bd.name ?? '');
    setSupportEmail(s.supportEmail ?? bd.email ?? '');
    setSupportPhone(s.supportPhone ?? bd.phone ?? '');
    setStoreAddress(s.storeAddress ?? formatAddress(bd.address));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const handleSave = async () => {
    try {
      await updateSettings({
        custom: {
          appSettings: { storeName, supportEmail, supportPhone, storeAddress },
        },
      }).unwrap();
      showSuccess();
    } catch (err) {
      showError(err?.data?.error?.message);
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="h6">Application Settings</Typography>
        <Typography variant="body2" color="text.secondary">
          Store identity and contact details used across the storefront and receipts.
        </Typography>
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Store Name"
          value={storeName}
          onChange={(e) => setStoreName(e.target.value)}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          type="email"
          label="Support Email"
          value={supportEmail}
          onChange={(e) => setSupportEmail(e.target.value)}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Support Phone"
          value={supportPhone}
          onChange={(e) => setSupportPhone(e.target.value)}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          label="Store Address"
          value={storeAddress}
          onChange={(e) => setStoreAddress(e.target.value)}
        />
      </Grid>

      <Grid item xs={12}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={isLoading}
        >
          {isLoading ? 'Saving...' : 'Save Settings'}
        </Button>
      </Grid>

      <SaveSnackbar snack={snack} onClose={closeSnack} />
    </Grid>
  );
};

export default AppSettings;
